import { useEffect, useState } from "react";
import axios from "../../api/axios";
import SidebarClient from "../../components/SidebarClient";
import { formatLocalDate, formatLocalDateTime } from "../../utils/dateTime";

export default function ClientDashboard() {
  const [data, setData] = useState({
    activeBots: 0,
    messagesSent: 0,
    delivered: 0,
    readCount: 0,
    failed: 0,
    pending: 0,
    apiStatus: "Checking...",
    planName: "Loading...",
    daysRemaining: null,
    planExpiryDate: null,
  });

  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchClientData = async () => {
      try {
        const res = await axios.get("/client/dashboard-stats");
        setData(res.data);
      } catch (err) {
        console.error("Client data fetch error:", err);
      }
    };

    const fetchRecent = async () => {
      setLoadingRecent(true);
      try {
        const res = await axios.get("/whatsapp/logs", { params: { limit: 20 } });
        setRecent(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Recent activity fetch error:", err);
        setRecent([]);
      }
      setLoadingRecent(false);
    };

    fetchClientData();
    fetchRecent();
  }, []);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await axios.get("/whatsapp/report/messages", {
        params: { format: "csv", limit: 2000 },
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "messages_report.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to download report");
    }
    setDownloading(false);
  };

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-50">
      {/* 1. Sidebar yahan add kiya hai */}
      <SidebarClient />

      {/* 2. Main Content Area */}
      <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto mt-14 lg:mt-0">
        <header className="flex flex-col md:flex-row md:justify-between md:items-center mb-8 md:mb-10 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">User Dashboard</h1>
            <p className="text-gray-500 mt-1">Manage your WhatsApp automation and bots.</p>
          </div>
          <div className={`${data.planName === 'No Plan' || (data.daysRemaining !== null && data.daysRemaining <= 0) ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'} px-4 py-2 rounded-full font-semibold text-sm`}>
            Plan: {data.planName}
            {data.daysRemaining !== null && (
              <span className={`ml-3 font-black ${data.daysRemaining <= 0 ? 'text-red-800' : 'text-blue-800'}`}>({data.daysRemaining} days left)</span>
            )}
          </div>
        </header>

        {/* Plan Alert Banner */}
        {(data.planName === "No Plan" || (data.daysRemaining !== null && data.daysRemaining <= 0)) && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p className="text-red-800 font-bold text-sm">
                  {data.planName === "No Plan" ? "No Active Subscription Plan" : "Your Subscription Plan Has Expired"}
                </p>
                <p className="text-red-600 text-xs">
                  You cannot connect WhatsApp or send messages without an active plan. Please subscribe or renew your plan.
                </p>
              </div>
            </div>
            <a
              href="/client/plan-renewal"
              className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-4 py-2 rounded-xl transition whitespace-nowrap"
            >
              Renew / Choose Plan →
            </a>
          </div>
        )}
        
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition space-y-2">
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Active Bots</p>
            <p className="text-3xl font-black text-blue-600 leading-none">{data.activeBots}</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition space-y-2">
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Messages Sent</p>
            <p className="text-3xl font-black text-green-600 leading-none">{data.messagesSent}</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition space-y-2">
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Delivered</p>
            <p className="text-3xl font-black text-emerald-600 leading-none">{data.delivered}</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition space-y-2">
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Read</p>
            <p className="text-3xl font-black text-purple-600 leading-none">{data.readCount}</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition space-y-2">
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Failed</p>
            <p className="text-3xl font-black text-red-600 leading-none">{data.failed}</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition space-y-2">
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Pending</p>
            <p className="text-3xl font-black text-yellow-600 leading-none">{data.pending}</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition space-y-2">
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">API Status</p>
            <p className="text-2xl font-black text-green-500 leading-none">{data.apiStatus}</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition space-y-2">
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Account ID</p>
            <p className="text-2xl font-black text-purple-600 leading-none">#{data.userId || "N/A"}</p>
            {data.planExpiryDate && (
              <p className="text-xs text-gray-500">Expires: {formatLocalDate(data.planExpiryDate)}</p>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-10 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800">Recent Activity</h3>
            <button
              onClick={downloadCsv}
              disabled={downloading}
              className="bg-gray-900 text-white px-4 py-2 rounded-xl font-bold hover:bg-gray-800 transition disabled:opacity-50"
            >
              {downloading ? "Downloading..." : "Download CSV"}
            </button>
          </div>

          {loadingRecent ? (
            <div className="flex justify-center py-10">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : recent.length === 0 ? (
            <div className="p-8 rounded-2xl border border-dashed border-gray-300 flex flex-col items-center justify-center text-center">
              <div className="bg-gray-100 p-4 rounded-full mb-4">🤖</div>
              <h3 className="text-xl font-bold text-gray-800">No Recent Activity</h3>
              <p className="text-gray-500 max-w-sm">
                Start by connecting your WhatsApp instance from the "WhatsApp Connect" menu.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-3 pr-4">Time</th>
                    <th className="py-3 pr-4">To</th>
                    <th className="py-3 pr-4">Type</th>
                    <th className="py-3 pr-4">Source</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 pr-4">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((m) => (
                    <tr key={m.id} className="border-b last:border-b-0">
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">
                        {formatLocalDateTime(m.created_at)}
                      </td>
                      <td className="py-3 pr-4 font-mono text-gray-800 whitespace-nowrap">{m.to_number}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{m.message_type}</td>
                      <td className="py-3 pr-4">
                        <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 font-bold">
                          {m.source || "MANUAL"}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        {(() => {
                          const statusColors = {
                            PENDING: "bg-yellow-100 text-yellow-800",
                            SENT: "bg-blue-100 text-blue-800",
                            DELIVERED: "bg-purple-100 text-purple-800",
                            READ: "bg-green-100 text-green-800",
                            PLAYED: "bg-green-100 text-green-800",
                            FAILED: "bg-red-100 text-red-800",
                          };
                          const color = statusColors[m.status] || "bg-gray-100 text-gray-800";
                          const statusIcons = {
                            PENDING: "⏳",
                            SENT: "✓",
                            DELIVERED: "✓✓",
                            READ: "✓✓",
                            PLAYED: "▶",
                            FAILED: "✗",
                          };
                          return (
                            <span className={`px-3 py-1 rounded-full font-bold ${color}`}>
                              {statusIcons[m.status] || ""} {m.status}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 pr-4 text-gray-700 max-w-md truncate" title={m.content || ""}>
                        {String(m.content || "").slice(0, 80)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}